import { useState, useCallback } from 'react';

/**
 * useMenuPosition Hook
 * 
 * Handles menu positioning and viewport boundary adjustments.
 */
export function useMenuPosition(btnRef, menuRef) {
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Reposition menu to stay within viewport
  const repositionMenu = useCallback(() => {
    if (!menuRef.current || !btnRef.current) return;
    
    const menuRect = menuRef.current.getBoundingClientRect();
    const btnRect = btnRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 8;
    
    let newTop = menuPosition.top;
    let newLeft = menuPosition.left;
    let needsUpdate = false;
    
    // Check bottom overflow
    if (menuRect.bottom > viewportHeight - padding) {
      // Try placing above the button
      const topAbove = btnRect.top - menuRect.height - 4;
      if (topAbove >= padding) {
        newTop = topAbove;
      } else {
        // Constrain to viewport
        newTop = viewportHeight - menuRect.height - padding;
      }
      needsUpdate = true;
    }
    
    // Check top overflow
    if (menuRect.top < padding) {
      newTop = padding;
      needsUpdate = true;
    }
    
    // Check right overflow
    if (menuRect.right > viewportWidth - padding) {
      newLeft = viewportWidth - menuRect.width - padding;
      needsUpdate = true;
    }
    
    // Check left overflow
    if (menuRect.left < padding) {
      newLeft = padding;
      needsUpdate = true;
    }
    
    if (needsUpdate) {
      setMenuPosition({ top: newTop, left: newLeft });
    }
  }, [menuPosition.top, menuPosition.left, btnRef, menuRef]);

  // Place from the pointer. Measuring a button inside the board picture
  // rebuilds that picture, and the menu cannot paint until the rebuild finishes.
  const openMenu = useCallback((event) => {
    const fromPointer = event && (event.clientX || event.clientY);
    if (fromPointer) {
      setMenuPosition(menuAnchor(event.clientX, event.clientY));
      return;
    }
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setMenuPosition(menuAnchor(rect.left, rect.bottom));
  }, [btnRef]);

  return {
    menuPosition,
    openMenu,
    repositionMenu,
  };
}

function menuAnchor(left, bottom) {
  const padding = 8;
  const width = 200;
  let x = left;
  if (x + width > window.innerWidth - padding) {
    x = Math.max(padding, window.innerWidth - width - padding);
  }
  if (x < padding) x = padding;
  return { top: bottom + 4, left: x };
}

export default useMenuPosition;

